import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase, SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

interface RehostBatchResponse {
  success: boolean;
  processed: number;
  failed: number;
  remaining: number;
  done: boolean;
  skippedLarge?: number;
  errors?: string[];
  error?: string;
}

export interface RehostResult {
  processed: number;
  failed: number;
  batches: number;
  skippedLarge: number;
}

// Safety-cap: aldri flere enn dette antall bunker i én kjøring (unngår
// uendelig loop hvis en asset konsekvent feiler men forblir i køen).
// Hver bunke er nå tidsbudsjett-begrenset (kan gjøre færre enn BATCH_SIZE),
// så caps må være romslig nok til å dekke hele køen i én kjøring.
const MAX_BATCHES = 100;

async function callBatch(): Promise<RehostBatchResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/rehost-monday-chat-assets`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token ?? ""}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: "{}",
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`rehost-monday-chat-assets (${res.status}): ${text.slice(0, 300)}`);
  }
  return (await res.json()) as RehostBatchResponse;
}

/**
 * VIB-963: re-hoster Monday-chatbilder til Storage, en bunke om gangen.
 * Looper edge-funksjonen til den er ferdig (eller en bunke ikke gjør
 * fremgang). Holder hver request under CPU-grensen som tidligere ga 546.
 */
export function useRehostChatAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<RehostResult> => {
      let processed = 0;
      let failed = 0;
      let batches = 0;
      let skippedLarge = 0;
      const toastId = toast.loading("Re-hoster chat-bilder …");
      try {
        for (let i = 0; i < MAX_BATCHES; i++) {
          const r = await callBatch();
          if (!r.success) throw new Error(r.error ?? "Re-hosting feilet");
          processed += r.processed;
          failed += r.failed;
          skippedLarge = r.skippedLarge ?? 0;
          batches++;
          toast.loading(`Re-hoster chat-bilder … ${processed} ferdig, ${r.remaining} igjen`, {
            id: toastId,
          });
          // Stopp hvis ferdig, eller hvis bunken verken klarte noe eller har mer
          // (alt som gjenstår feiler konsekvent → unngå uendelig loop).
          if (r.done || r.remaining === 0) break;
          if (r.processed === 0) break;
        }
        toast.dismiss(toastId);
      } catch (e) {
        toast.dismiss(toastId);
        throw e;
      }
      return { processed, failed, batches, skippedLarge };
    },
    onSuccess: async (r) => {
      await invalidateFor(qc, "project_comments");
      toast.success(
        `Re-hosting fullført: ${r.processed} bilder` +
          `${r.failed > 0 ? `, ${r.failed} feilet` : ""}` +
          `${r.skippedLarge > 0 ? `. ${r.skippedLarge} store filer (>20 MB) ble hoppet over` : ""}`,
      );
    },
    onError: (err) => {
      toast.error(`Re-hosting feilet: ${(err as Error).message}`);
    },
  });
}
